import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Card from "@/components/foundation/Card";

/**
 * RoadmapCard — one phase card in the roadmap timeline.
 *
 * Props:
 *   phase: string (e.g. "01")
 *   title: string
 *   status: "live" | "in-development" | "vision"
 *   features: string[]
 *   index: number (for stagger)
 */
const STATUS_CONFIG = {
  live: {
    label: "Live",
    dot: "bg-brand-emerald shadow-[0_0_10px_rgba(0,230,118,0.7)]",
    pillClass:
      "border-brand-emerald/25 bg-brand-emerald/10 text-brand-emerald",
    animated: true,
  },
  "in-development": {
    label: "In Development",
    dot: "bg-white/50",
    pillClass:
      "border-white/[0.12] bg-white/[0.04] text-text-secondary",
    animated: false,
  },
  vision: {
    label: "Vision",
    dot: "bg-transparent border border-white/40",
    pillClass:
      "border-white/[0.10] bg-transparent text-text-muted",
    animated: false,
  },
};

export const RoadmapCard = ({
  phase,
  title,
  status = "vision",
  features = [],
  index = 0,
  className,
}) => {
  const cfg = STATUS_CONFIG[status];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{
        duration: 0.6,
        delay: 0.1 * index,
        ease: [0.22, 1, 0.36, 1],
      }}
      className={cn("h-full", className)}
      data-testid={`roadmap-card-${status}`}
    >
      <Card variant="elevated" className="flex h-full flex-col p-8">
        {/* Phase number + status pill */}
        <div className="flex items-center justify-between">
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-brand-emerald">
            Phase {phase}
          </span>
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.14em]",
              cfg.pillClass,
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                cfg.dot,
                cfg.animated && "animate-pulse-glow",
              )}
            />
            {cfg.label}
          </span>
        </div>

        {/* Big phase number */}
        <div className="mt-8 font-display text-6xl font-semibold leading-none tracking-tight text-white/95">
          {phase}
        </div>

        {/* Title */}
        <h3 className="mt-3 font-display text-2xl font-semibold leading-tight text-white">
          {title}
        </h3>

        {/* Divider */}
        <div className="my-6 h-px w-full bg-white/[0.06]" />

        {/* Features */}
        <ul className="flex flex-col gap-3">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className={cn(
                  "mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full",
                  status === "live"
                    ? "bg-brand-emerald text-black"
                    : "border border-white/[0.12] text-text-muted",
                )}
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </span>
              <span className="text-sm text-text-secondary">{f}</span>
            </li>
          ))}
        </ul>
      </Card>
    </motion.div>
  );
};

export default RoadmapCard;
